import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import ChartDataLabels from "chartjs-plugin-datalabels"

// Renders a multi-series bar chart for the Distribution Trend report.
// One grouped bar set per top-level row group, cycles on the x-axis,
// selected measure on the y-axis. Config is JSON-serialised from the view partial.
//
// Example:
//   <div data-controller="distribution-trend-chart"
//        data-distribution-trend-chart-config-value="{ ... }">
//     <canvas data-distribution-trend-chart-target="canvas"></canvas>
//   </div>
export default class extends Controller {
  static targets = ["canvas"]
  static values  = { config: Object }

  connect() {
    const cfg = this.configValue
    const isPercent = !!cfg.is_percent

    // Apply rounded edges + force the type to bar (not line). The source partial
    // passes line-style fields (fill, tension) -- harmless on bar but we make sure
    // each dataset advertises rounded corners.
    const datasets = (cfg.datasets || []).map((ds) => ({
      borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
      borderSkipped: false,
      ...ds
    }))

    this.chart = new Chart(this.canvasTarget, {
      type: "bar",
      plugins: [ChartDataLabels],
      data: {
        labels: cfg.labels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: isPercent ? {
              label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(1)}%`
            } : {}
          },
          datalabels: {
            anchor: "end",
            align: "start",
            offset: 4,
            color: "#ffffff",
            font: { weight: "bold", size: 14 },
            display: (ctx) => {
              const v = ctx.dataset.data[ctx.dataIndex]
              return v !== null && v !== undefined && v !== 0
            },
            formatter: (value) => {
              if (value === null || value === undefined) return ""
              return isPercent
                ? `${Number(value).toFixed(1)}%`
                : Number(value).toLocaleString()
            }
          }
        },
        scales: {
          x: {
            grid: { display: false }
          },
          y: {
            beginAtZero: true,
            grid: { display: false },
            ticks: { display: false }
          }
        }
      }
    })
  }

  disconnect() {
    if (this.chart) { this.chart.destroy(); this.chart = null }
  }
}
